// Charges — the visits charge capture could not finish on its own. Registry page
// (features/registry.js, `feature: 'charge_templates'`); App.js renders <FeatureOff/>
// when the feature is off.
//
// Two lists, both reads:
//   - Needs a person: capture drafts with no lines, or held because the note is not
//     signed — each with the reason and the template that would have matched. A
//     person opens the draft in the claim editor or copies the last charge onto it.
//   - Pending: attended visits with no charge at all, and the ones a do-not-bill
//     template set aside.
//
// Nothing on this page creates, submits or prices a claim on its own. The one write
// is Copy last charge, which asks before it replaces a draft's lines.
//
// Contract: docs/charge-capture.md "API" (Instance C, routes/charges.py).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatPatientName } from "../../utils/formatName";
import { openClaimEditor } from "../claims/claimEditorTarget";
import { ReadNoteButton } from "../notes";
import CopyLastChargeButton from "./CopyLastChargeButton";
import ChargeTemplatesCard from "./ChargeTemplatesCard";
import { fetchNeedsPerson, fetchPending } from "./api";
import "./Charges.css";

const TABS = [
  { key: "needs", label: "Needs a person" },
  { key: "pending", label: "Visits without a charge" },
];
// The charge templates moved here from Practice Details' Billing Settings (David,
// 2026-10-25): the rules that make the drafts this page lists. Staff only, and only
// while the practice has `features.charge_templates`.
const TEMPLATES_TAB = { key: "templates", label: "Templates" };

function isoDay(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function noteLabel(value) {
  if (value === true) return "Note present";
  if (value === false) return "No note";
  return "Note unknown";
}

function Facts({ row }) {
  return (
    <div className="chg-facts">
      {row.visitKind && <span className="chg-chip muted">{row.visitKind}</span>}
      {row.sourceColor && (
        <span className="chg-chip muted">colour: {row.sourceColor}</span>
      )}
      <span
        className={`chg-chip ${row.sourceNotePresent === false ? "warn" : "muted"}`}
      >
        {noteLabel(row.sourceNotePresent)}
      </span>
    </div>
  );
}

export default function ChargesPage({
  selectedPractice,
  features = {},
  isAdmin = false,
  readOnly = false,
  onPageChange,
}) {
  const practiceId = selectedPractice?.id;
  const [tab, setTab] = useState("needs");
  const [needs, setNeeds] = useState(null);
  const [pending, setPending] = useState(null);
  const [end, setEnd] = useState(() => isoDay(new Date()));
  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return isoDay(d);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ticket = useRef(0);
  const practiceRef = useRef(practiceId);
  practiceRef.current = practiceId;

  useEffect(() => {
    setNeeds(null);
    setPending(null);
    setError(null);
  }, [practiceId]);

  const load = useCallback(async () => {
    if (!practiceId || practiceId !== practiceRef.current || tab === "templates") return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      if (tab === "needs") {
        const body = await fetchNeedsPerson(practiceId);
        if (mine !== ticket.current || practiceId !== practiceRef.current)
          return;
        setNeeds(body);
      } else {
        const body = await fetchPending(practiceId, { start, end });
        if (mine !== ticket.current || practiceId !== practiceRef.current)
          return;
        setPending(body);
      }
    } catch (err) {
      if (mine !== ticket.current || practiceId !== practiceRef.current) return;
      if (tab === "needs") setNeeds(null);
      else setPending(null);
      setError(err.message);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId, tab, start, end]);

  useEffect(() => {
    load();
  }, [load]);

  if (!practiceId) {
    return (
      <div className="chg-page">
        <p className="chg-empty">Select a practice to see charges waiting.</p>
      </div>
    );
  }

  const canOpenEditor = !!(onPageChange && features.claim_editor);
  const tabs = !readOnly && features.charge_templates ? [...TABS, TEMPLATES_TAB] : TABS;

  return (
    <div className="chg-page">
      <div className="chg-header">
        <div>
          <h1>Charges</h1>
          <p className="chg-sub">
            Visits {selectedPractice.name}'s charge templates could not turn
            into a finished draft. Nothing here creates or submits a claim on
            its own.
          </p>
        </div>
        {tab !== "templates" && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        )}
      </div>

      <div className="chg-filters">
        <div className="chg-tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              className={`chg-tab${tab === t.key ? " active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {t.key === "needs" && needs && (
                <span className="chg-tab-count">{needs.drafts.length}</span>
              )}
            </button>
          ))}
        </div>
        {tab === "pending" && (
          <>
            <label className="chg-inline-field">
              <span>From</span>
              <input
                type="date"
                value={start}
                max={end}
                onChange={(e) => e.target.value && setStart(e.target.value)}
              />
            </label>
            <label className="chg-inline-field">
              <span>To</span>
              <input
                type="date"
                value={end}
                min={start}
                onChange={(e) => e.target.value && setEnd(e.target.value)}
              />
            </label>
          </>
        )}
      </div>

      {error && (
        <div className="chg-message error" role="alert">
          {error}
        </div>
      )}

      {tab === "needs" && needs && (
        <>
          {needs.truncated && (
            <div className="chg-message warn">
              Showing the oldest 1,000 drafts. Work these down to see the rest.
            </div>
          )}
          {needs.drafts.length === 0 ? (
            <p className="chg-empty">No draft is waiting for a person.</p>
          ) : (
            <div className="chg-table-wrap">
              <table className="chg-table">
                <thead>
                  <tr>
                    <th>Date of service</th>
                    <th>Patient</th>
                    <th>Provider</th>
                    <th>Why it needs a person</th>
                    <th>Template</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {needs.drafts.map((row) => (
                    <tr key={row.claimId}>
                      <td className="chg-nowrap">{row.dateOfService || "—"}</td>
                      <td>
                        {formatPatientName(row.patientName)}
                        <Facts row={row} />
                      </td>
                      <td>{row.providerName || "—"}</td>
                      <td>
                        {row.reason || "—"}
                        {row.flags.length > 0 && (
                          <div className="chg-muted">
                            {row.flags.join(", ")}
                          </div>
                        )}
                      </td>
                      <td>
                        {row.template ? (
                          row.template.name
                        ) : (
                          <span className="chg-muted">None matched</span>
                        )}
                      </td>
                      <td className="chg-actions">
                        {canOpenEditor && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() =>
                              openClaimEditor(
                                row.claimId,
                                onPageChange,
                                practiceId,
                              )
                            }
                          >
                            Open draft
                          </button>
                        )}
                        <ReadNoteButton practiceId={practiceId} claimId={row.claimId} notePresent={row.sourceNotePresent} features={features} readOnly={readOnly} onPageChange={onPageChange} />
                        <CopyLastChargeButton
                          practiceId={practiceId}
                          claim={{ id: row.claimId, claim_status: "draft" }}
                          readOnly={readOnly}
                          onCopied={load}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "pending" && pending && (
        <>
          {pending.truncated && (
            <div className="chg-message warn">
              This range has more visits than can be shown. Narrow the dates.
            </div>
          )}
          <h2 className="chg-section">Visits without a charge</h2>
          {pending.visits.length === 0 ? (
            <p className="chg-empty">
              Every attended visit in this range has a charge.
            </p>
          ) : (
            <div className="chg-table-wrap">
              <table className="chg-table">
                <thead>
                  <tr>
                    <th>Date of service</th>
                    <th>Patient</th>
                    <th>Provider</th>
                    <th>Template</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.visits.map((row) => (
                    <tr key={`v-${row.appointmentId}`}>
                      <td className="chg-nowrap">{row.dateOfService || "—"}</td>
                      <td>
                        {formatPatientName(row.patientName)}
                        <Facts row={row} />
                      </td>
                      <td>{row.providerName || "—"}</td>
                      <td>
                        {row.template ? (
                          row.template.name
                        ) : (
                          <span className="chg-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pending.notBillable.length > 0 && (
            <>
              <h2 className="chg-section">Set aside as not billable</h2>
              <div className="chg-table-wrap">
                <table className="chg-table">
                  <thead>
                    <tr>
                      <th>Date of service</th>
                      <th>Patient</th>
                      <th>Provider</th>
                      <th>Template</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.notBillable.map((row) => (
                      <tr key={`nb-${row.appointmentId}`}>
                        <td className="chg-nowrap">
                          {row.dateOfService || "—"}
                        </td>
                        <td>{formatPatientName(row.patientName)}</td>
                        <td>{row.providerName || "—"}</td>
                        <td>{row.template ? row.template.name : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {tab === "templates" && <ChargeTemplatesCard practiceId={practiceId} isAdmin={isAdmin} />}
    </div>
  );
}
